import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, EventEmitter, HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit, Output,
  SimpleChanges
} from '@angular/core';
import { ComponentType } from '@angular/cdk/overlay';
import { Estimate, EstimateVersion } from "../../models/estimate.model";
import { EstimatesService } from "../../services/estimates.service";
import { moveItemInArray } from "@angular/cdk/drag-drop";
import { ModalBehavior, ModalsService } from "projects/common/src/lib/services/modals.service";
import { MatLegacySnackBar as MatSnackBar } from "@angular/material/legacy-snack-bar";
import { showSnackbar } from "projects/common/src/lib/components/snackbar/snackbar.component";
import {
  CreateEditWorkflowItemComponent
} from "../../modals/create-edit-workflow-item/create-edit-workflow-item.component";
import { Observable, Subject, Subscription, timer } from "rxjs";
import { debounce, filter, map, tap } from "rxjs/operators";
import { WorkflowItemInfoMobileComponent } from "../workflow-item-info-mobile/workflow-item-info-mobile.component";
import { CommonEoIItem } from "../../models/eoi.model";
import { digitsOnly, onIntegerPaste } from "../../helpers/forms";
import { UsersService } from "../../services/users.service";
import {
  CreateEditWorkflowItemRestrictedComponent
} from "../../modals/create-edit-workflow-item-restricted/create-edit-workflow-item-restricted.component";
import { BusinessService } from "../../services/business.service";

@Component({
  selector: 'app-estimate-items-list',
  templateUrl: './estimate-items-list.component.html',
  styleUrls: ['./estimate-items-list.component.scss']
})
export class EstimateItemsListComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  mobileView = false;
  @HostListener('window:resize')
  onResize() {
    this.mobileView = window.innerWidth < 576;
  }

  protected readonly digitsOnly = digitsOnly;
  protected readonly onPaste = onIntegerPaste;

  @Input() estimate!: Estimate;
  @Input() estimateVersion!: EstimateVersion;
  @Input() scrollPosition = 0;
  @Input() readonly = false;

  @Output() itemsListFocus = new EventEmitter<boolean>();

  dragging: boolean = false;

  hoveredItemIndex: number | null = null;
  infoIconDistancesFromTop: number[] = [];

  afterViewInit = false;

  qtyFocus$ = new Subject<boolean>();
  qtyFocusSub!: Subscription;

  fullItemAccess$: Observable<boolean> = this.usersService.currentUser$.pipe(
    filter(value => !!value),
    map(userProfile => userProfile.role !== 'user' || userProfile.permissions.includes('edit_items'))
  );

  businessId$ = this.businessService.selectedBusiness$.pipe(
    filter(businessProfile => !!businessProfile),
    map(businessProfile => businessProfile!.businessId)
  );

  constructor(
    private estimatesService: EstimatesService,
    private modalsService: ModalsService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private usersService: UsersService,
    private businessService: BusinessService
  ) {
    this.mobileView = window.innerWidth < 576;
  }

  ngOnInit(){
    this.qtyFocusSub = this.qtyFocus$.pipe(
      tap(focus => {
        this.itemsListFocus.emit(focus);
        if (!focus && this.estimateVersion.items)
          this.estimateVersion.items.forEach(item => {
            if (item.quantity === 0 || !item.quantity)
              item.quantity = 1;
          });
      }),
      debounce(focus => {
        return focus ? timer(20) : timer(0);
      })
    ).subscribe(async focus => {
      if (!focus)
        await this.saveEstimateVersion();
    });
  }

  ngOnDestroy() {
    this.qtyFocusSub.unsubscribe();
  }

  ngAfterViewInit() {
    if (!this.mobileView) {
      if (this.estimateVersion.items)
        for (let i = 0; i < this.estimateVersion.items.length; i++)
          this.infoIconDistancesFromTop.push(
            document.getElementById('info-icon-'+i)!.getBoundingClientRect().top
          );
      this.cdr.detectChanges();
      this.afterViewInit = true;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.estimateVersion.items)
      return;

    if ('estimateVersion' in changes) {
      for (let i = 0; i < this.estimateVersion.items.length; i++) {
        this.reCalcQtyPricePadding('qty-price-'+i);
      }
    }

    if (this.afterViewInit) {
      for (let i = 0; i < this.estimateVersion.items.length; i++) {
        const top = document.getElementById('info-icon-' + i)?.getBoundingClientRect().top;
        if(top)
          this.infoIconDistancesFromTop[i] = top;
      }
    }
  }

  reCalcQtyPricePadding(elementId: string){
    if (!this.mobileView) {
      setTimeout(() => {
        try {
          const textLength = document.getElementById(elementId)
            ?.getElementsByClassName('item-price')[0].textContent?.length!-4
          const qtyPriceBlock = document.getElementById(elementId);
          if (textLength > 8 && textLength < 10) {
            qtyPriceBlock!.classList.add('sp-s');
          } else if (textLength >= 10 && textLength < 12) {
            qtyPriceBlock!.classList.add('sp-m');
          } else if (textLength >= 12) {
            qtyPriceBlock!.classList.add('sp-l');
          } else {
            qtyPriceBlock!.classList.remove('sp-l');
            qtyPriceBlock!.classList.remove('sp-m');
            qtyPriceBlock!.classList.remove('sp-s');
          }
        } catch (e) {
          console.log('Error during calculation!')
        }
      }, 0);
    }
  }

  getItemPrice(estimateItem: CommonEoIItem): string {
    if (!estimateItem.quantity || estimateItem.quantity === 0) {
      return '0';
    } else {
      let total = estimateItem.price!;

      if (estimateItem.discountAmount || estimateItem.discountPercent) {
        if (estimateItem.discountType === 'percent') {
          total -= total*estimateItem.discountPercent!/100;
        } else {
          total -= estimateItem.discountAmount!;
        }
      }

      if (estimateItem.taxable) {
        total += total*this.estimateVersion.clientSalesTaxPercentage!/100;
      }

      total = estimateItem.quantity!*total;

      return (Math.round(total * 100) / 100).toFixed(2).toString();
    }
  }

  async qtyFieldInput(event: any, estimateItem: CommonEoIItem, mobile?: boolean, elementId?: string) {
    event.target.value = event.target.value?.replace(/\D/, ''); // Prevent input non-digit chars by codes using ALT key
    setTimeout(async () => {
      estimateItem.quantity = +event.target.value;
      if (!mobile)
        this.reCalcQtyPricePadding(elementId!);
    }, 10);
  }

  async dropAction(event: any) {
    try {
      moveItemInArray(this.estimateVersion.items!, event.previousIndex, event.currentIndex);
      this.fixItemsIndexes();
      await this.saveEstimateVersion();
    } catch (e) {
      alert('Something went wrong during estimates update!');
    }
  }

  async openAddEstimateItemModal(fullAccess: boolean) {
    await this.modalsService.open(
      (fullAccess ? CreateEditWorkflowItemComponent : CreateEditWorkflowItemRestrictedComponent) as ComponentType<any>,
      {
        behavior: ModalBehavior.Dialog,
        disableClose: true,
        data: {
          salesTaxPercentage: this.estimateVersion.clientSalesTaxPercentage,
          workflow: this.estimate,
          workflowVersion: this.estimateVersion,
          mode: 'add',
          updateFunction: (async (estimateId: number, updateData: Partial<EstimateVersion>) =>
            this.estimatesService.updateEstimateVersion(estimateId, updateData)).bind(this)
        }
      }
    );
  }

  async editItem(item: CommonEoIItem, fullAccess: boolean) {
    await this.modalsService.open(
      (fullAccess ? CreateEditWorkflowItemComponent : CreateEditWorkflowItemRestrictedComponent) as ComponentType<any>,
      {
        behavior: ModalBehavior.Dialog,
        disableClose: true,
        data: {
          salesTaxPercentage: this.estimateVersion.clientSalesTaxPercentage,
          item: item,
          workflow: this.estimate,
          workflowVersion: this.estimateVersion,
          mode: 'edit',
          updateFunction: (async (estimateId: number, updateData: Partial<EstimateVersion>) =>
            this.estimatesService.updateEstimateVersion(estimateId, updateData)).bind(this)
        }
      }
    );
  }

  fixItemsIndexes() {
    for (let i = 0; i < this.estimateVersion.items!.length; i++) {
      this.estimateVersion.items![i].index = i;
    }
  }

  async duplicateItem(item: CommonEoIItem, index: number) {
    const newItem = { ... item };
    this.estimateVersion.items?.splice(index + 1, 0, newItem);
    if (index !== this.estimateVersion.items!.length - 1)
      this.fixItemsIndexes();
    await this.saveEstimateVersion();
    showSnackbar(this.snackBar, {
      message: 'Item Duplicated',
      duration: 2000
    });
  }

  async deleteItem(index: number) {
    const currentItemsState = JSON.parse(JSON.stringify(this.estimateVersion.items));
    this.estimateVersion.items!.splice(index, 1);
    if (index !== this.estimateVersion.items!.length)
      this.fixItemsIndexes();
    await this.saveEstimateVersion();
    showSnackbar(this.snackBar, {
      message: 'Item deleted',
      duration: 10000,
      actionText: 'Undo',
      action: (async () => {
        this.estimateVersion.items = currentItemsState;
        await this.saveEstimateVersion();
      }).bind(this),
    });
  }

  private async saveEstimateVersion() {
    this.estimatesService._recalculate$.next(0);
    await this.estimatesService.updateEstimateVersion(this.estimateVersion.id, { items: this.estimateVersion.items } );
  }

  async openItemInfoMobile(estimateItem: CommonEoIItem) {
    await this.modalsService.open(WorkflowItemInfoMobileComponent, {
      data: {
        salesTaxPercentage: this.estimateVersion.clientSalesTaxPercentage,
        item: estimateItem
      }
    });
  }
}
